import React, { useEffect, useState } from "react";
import { api, formatUAH, formatNum } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import PeriodFilter from "@/components/PeriodFilter";

export default function Referrals() {
  const [period, setPeriod] = useState({});
  const [roleFilter, setRoleFilter] = useState("all");
  const [leaderboard, setLeaderboard] = useState([]);
  const [bySource, setBySource] = useState([]);

  useEffect(() => {
    if (period.from === undefined && period.to === undefined) return;
    const params = new URLSearchParams();
    if (period.from) params.set("from_date", period.from);
    if (period.to) params.set("to_date", period.to);
    if (roleFilter !== "all") params.set("role", roleFilter);
    Promise.all([
      api.get(`/referrals/leaderboard?${params}`),
      api.get(`/referrals/by-source?${params}`),
    ]).then(([l, s]) => { setLeaderboard(l.data); setBySource(s.data); });
  }, [period, roleFilter]);

  return (
    <div data-testid="referrals-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">Роль</Label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-44" data-testid="referrals-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Усі ролі</SelectItem>
                <SelectItem value="doctor">Лікарі</SelectItem>
                <SelectItem value="manager">Менеджери</SelectItem>
                <SelectItem value="paramedic">Фельдшери</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} defaultPreset="ytd" />
      </div>

      <Card className="gv-card"><CardContent className="p-0 overflow-x-auto">
        <div className="p-5 border-b border-slate-200">
          <div className="heading font-semibold text-[#0F2027]">Рейтинг направлень</div>
          <div className="text-xs text-slate-500">Сортовано по виручці. Конверсія = операції / направлення.</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-4">#</th>
              <th className="text-left p-4">ПІБ</th>
              <th className="text-left p-4">Роль</th>
              <th className="text-right p-4">Направлень</th>
              <th className="text-right p-4">Прооперовано</th>
              <th className="text-right p-4">Конверсія</th>
              <th className="text-right p-4">Середній чек</th>
              <th className="text-right p-4">Виручка</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((r, idx) => (
              <tr key={r.employee_id} className="border-t border-slate-100" data-testid={`referrer-${r.employee_id}`}>
                <td className="p-4 text-slate-400 font-bold">{idx + 1}</td>
                <td className="p-4 font-medium">{r.full_name}</td>
                <td className="p-4"><Badge variant="secondary" className="bg-teal-50 text-teal-700 hover:bg-teal-50">{r.role_label}</Badge></td>
                <td className="p-4 text-right tabular-nums">{formatNum(r.referrals)}</td>
                <td className="p-4 text-right tabular-nums">{formatNum(r.operated)}</td>
                <td className="p-4 text-right tabular-nums">{r.conversion}%</td>
                <td className="p-4 text-right tabular-nums">{formatUAH(r.avg_check)}</td>
                <td className="p-4 text-right tabular-nums font-semibold text-[#0D9488]">{formatUAH(r.revenue)}</td>
              </tr>
            ))}
            {leaderboard.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">Немає даних</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Card className="gv-card"><CardContent className="p-0 overflow-x-auto">
        <div className="p-5 border-b border-slate-200"><div className="heading font-semibold text-[#0F2027]">По джерелах направлення</div></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-4">Джерело</th>
              <th className="text-right p-4">Направлень</th>
              <th className="text-right p-4">Прооперовано</th>
              <th className="text-right p-4">Конверсія</th>
              <th className="text-right p-4">Виручка</th>
            </tr>
          </thead>
          <tbody>
            {bySource.map((r) => (
              <tr key={r.source} className="border-t border-slate-100">
                <td className="p-4 font-medium">{r.source_label}</td>
                <td className="p-4 text-right tabular-nums">{formatNum(r.referrals)}</td>
                <td className="p-4 text-right tabular-nums">{formatNum(r.operated)}</td>
                <td className="p-4 text-right tabular-nums">{r.conversion}%</td>
                <td className="p-4 text-right tabular-nums font-semibold text-[#0D9488]">{formatUAH(r.revenue)}</td>
              </tr>
            ))}
            {bySource.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Немає даних</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
